import type { Location, NavigateFunction, NavigateOptions, To } from 'react-router-dom';

export interface MobileBackState {
  __backPath?: string;
  __backState?: unknown;
}

export function getCurrentRoute(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function createBackState(location: Location): MobileBackState {
  return {
    __backPath: getCurrentRoute(location),
    __backState: location.state ?? null,
  };
}

export function navigateWithBack(
  navigate: NavigateFunction,
  location: Location,
  to: To,
  options: Omit<NavigateOptions, 'state'> & { state?: Record<string, unknown> } = {}
): void {
  const { replace = true, state, ...restOptions } = options;
  navigate(to, {
    ...restOptions,
    replace,
    state: {
      ...(state ?? {}),
      ...createBackState(location),
    },
  });
}

export function navigateReplace(
  navigate: NavigateFunction,
  to: To,
  options: NavigateOptions = {}
): void {
  const { replace = true, ...restOptions } = options;
  navigate(to, { ...restOptions, replace });
}

export function resolveBackState(state: unknown): MobileBackState | null {
  if (!state || typeof state !== 'object') {
    return null;
  }
  const routeState = state as MobileBackState;
  if (!routeState.__backPath) {
    return null;
  }
  return routeState;
}

export function navigateBackWithFallback(
  navigate: NavigateFunction,
  location: Location,
  backFallbackPath?: To
): void {
  const routeState = resolveBackState(location.state);
  if (routeState?.__backPath) {
    navigate(routeState.__backPath, {
      replace: true,
      state: routeState.__backState,
    });
    return;
  }
  if (typeof window !== 'undefined' && window.history.length > 1) {
    navigate(-1);
    return;
  }
  if (backFallbackPath) {
    navigate(backFallbackPath, { replace: true });
  }
}
